import { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

import ActionButton from "../buttons/ActionButton";
import OptionList from "./OptionList";
import StudentInfoDao from "../../../dao/StudentInfoDao";
import UserDao from "../../../dao/UserDao";

import { GlobalContext } from "../../contextProvider/ContextProvider";

import "../../../styles/StudentAutoSuggest.scss";

/**
 * Load student detail for search: try active first (has address), then basic.
 * Active responds 400 when there is no current enrollment; fall back in that case.
 * @param {number} studentNumber
 * @param {string} token
 * @returns {Promise<{data?: {payload?: object}}|null>}
 */
const fetchStudentDetailPreferActive = async (studentNumber, token) => {
    try {
        const response = await StudentInfoDao({
            action: "studentInfoByNumberGet",
            studentNumber,
            token
        });
        if (response?.data?.payload) {
            return response;
        }
    } catch (error) {
        const status = error?.response?.status;
        if (status !== 400 && status !== 404) {
            console.error("Unable to retrieve active student detail:\n", error);
        }
    }

    try {
        return await StudentInfoDao({
            action: "studentInfoBasicByNumberGet",
            studentNumber,
            token
        });
    } catch (error) {
        console.error("Unable to retrieve basic student detail:\n", error);
        return null;
    }
};

/**
 * Display a textfield that responds to user input by showing a list of
 * addresses matching the address entered (after 4 characters)
 * @name StudentAutoSuggest
 * @param {func} formDispatch
 * @param {{}} formState
 * @param {func} handleChange
 * @param {string} id
 * @param {string} label
 * @param {string} name
 * @param {{}} selectedStudent
 * @param {func} setLoader
 * @param {[]} setResultMessages
 * @param {func} setSelectedStudent
 * @param {func} setStudentSearchResults
 * @param {[]} studentSearchResults
 * @returns {JSX.element}
 * @constructor
 */
const AutoSuggest = ({
    formDispatch,
    formState,
    handleChange,
    id,
    label,
    name,
    selectedStudent,
    setLoader,
    setResultMessages,
    setSelectedStudent,
    setStudentSearchResults,
    studentSearchResults
}) => {
    const { state } = useContext(GlobalContext);

    const { token } = state || {};

    const [filteredOptions, setFilteredOptions] = useState([]);
    const [showOptions, setShowOptions] = useState(false);
    const [showResetButton, setShowResetButton] = useState(false);

    const [chosenStudent, setChosenStudent] = useState(false);
    const showChosenStudent = chosenStudent || !!formState.studentNumber || !!selectedStudent;

    /**
     * When a student in the options list is clicked show
     * choose and send their info to formState
     * @name handleChooseStudent
     * @param {number} index
     */
    const handleChooseStudent = (index) => {
        setChosenStudent(true);
        setShowOptions(false);
        // Clear prior detail immediately so the UI doesn't show a stale student while loading.
        setSelectedStudent(null);
        setLoader(true);

        const student = filteredOptions[index];
        const firstName = student?.firstName;
        const lastName = student?.lastName;
        const studentNumber = student.studentNumber;
        const studentSearch = `${student.lastName}, ${student.firstName}`;

        formDispatch({
            type: "reset",
            payload: {
                ...formState,
                firstName,
                lastName,
                studentNumber,
                studentSearch
            }
        });
    };

    /**
     * Clear the search field and all other fields when clicking button
     * Reset the text input to initial and remove the options list
     */
    const resetAutoSuggest = () => {
        setChosenStudent(false);
        setShowOptions(false);
        setShowResetButton(false);
        setStudentSearchResults(null);
        setSelectedStudent(null);
        setResultMessages([]);
        setLoader(false);

        formDispatch({
            type: "reset",
            payload: {
                ...formState,
                firstName: "",
                lastName: "",
                studentNumber: "",
                studentSearch: ""
            }
        });
    };

    /**
     * Format filteredOptions if we have students coming back in the studentAutosuggest
     */
    useEffect(() => {
        if (studentSearchResults) {
            const filteredStudents = () => {
                const { results } = studentSearchResults;
                const tempResults = results.reduce((result, dto) => {
                    const { firstName, lastName, studentNumber } = dto;
                    if (
                        firstName?.toLowerCase().includes(formState.studentSearch?.toLowerCase()) ||
                        lastName?.toLowerCase().includes(formState.studentSearch?.toLowerCase()) ||
                        studentNumber?.includes(formState.studentSearch)
                    ) {
                        const tempDto = {
                            firstName,
                            lastName,
                            studentNumber
                        };
                        result.push(tempDto);
                    }

                    return result;
                }, []);
                // sort results by lastName:asc, firstName:asc, and studentNumber:desc
                tempResults.sort((a, b) => {
                    if (a.lastName === b.lastName) {
                        if (a.firstName === b.firstName) {
                            return a.studentNumber < b.studentNumber ? 1 : -1;
                        }

                        return a.firstName.localeCompare(b.firstName);
                    }

                    return a.lastName.localeCompare(b.lastName);
                });
                setFilteredOptions([...tempResults]);
            };

            filteredStudents();
        } else {
            setFilteredOptions([]);
        }
    }, [formState.studentSearch, studentSearchResults]);

    /**
     * If we get a token, call to get the list of students after the user has
     * entered at least 3 characters
     */
    useEffect(() => {
        if (chosenStudent || formState.studentSearch.length < 3 || !token) {
            return;
        }

        const fetchStudents = async () => {
            try {
                const params = {
                    numRows: 1000,
                    searchString: `*${formState.studentSearch}*`,
                    sort: "lastName:asc,firstName:asc,studentNumber:asc"
                };
                const options = {
                    action: "userSearchableStudentsRead",
                    params,
                    token
                };
                const response = await UserDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    setStudentSearchResults(payload);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setShowOptions(true);
            }
        };

        fetchStudents();
    }, [chosenStudent, formState.studentSearch, setShowOptions, setStudentSearchResults, token]);

    /**
     * Get the students information based on the studentNumber that is
     * in formState after the user clicks a student from the dropdown.
     * HandleChooseStudent sets the student details in formState first.
     */
    useEffect(() => {
        if (!formState.studentNumber || !token) {
            return;
        }

        let cancelled = false;

        const fetchStudentInfo = async () => {
            setLoader(true);
            try {
                const response = await fetchStudentDetailPreferActive(parseInt(formState.studentNumber, 10), token);
                if (cancelled) {
                    return;
                }
                if (response?.data?.payload) {
                    setSelectedStudent(response.data.payload);
                } else {
                    toast.error("There was a problem loading student details. Please try again later.");
                }
            } catch (error) {
                if (!cancelled) {
                    console.error("Unable to retrieve student detail:\n", error);
                    toast.error("There was a problem loading student details. Please try again later.");
                }
            } finally {
                if (!cancelled) {
                    setLoader(false);
                    setFilteredOptions([]);
                    setShowOptions(false);
                }
            }
        };

        fetchStudentInfo();

        return () => {
            cancelled = true;
        };
    }, [formState.studentNumber, setLoader, setSelectedStudent, token]);

    /**
     * Showing the reset button in the auto suggest (address list)
     */
    useEffect(() => {
        if (formState.studentSearch?.length > 1) {
            setShowResetButton(true);
        } else {
            setShowResetButton(false);
        }
    }, [formState.studentSearch, showResetButton]);

    return (
        <div>
            <div style={{ display: "flex", flexDirection: "column" }}>
                <div>
                    <div className="mt-2 mb-3">
                        Search by student number, first name, or last name. Student name for chosen student will show in
                        text field.
                    </div>
                    <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start" }}>
                        {showChosenStudent ? (
                            <div className="chosen-student-search-container">
                                <div className="chosen-student-container">
                                    <span className="chosen-student-text">
                                        <span>{formState.studentSearch}</span>
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex">
                                <label className="sr-only" htmlFor={id}>
                                    {label}{" "}
                                    <input
                                        id={id}
                                        name={name}
                                        onChange={handleChange}
                                        type="text"
                                        value={formState?.studentSearch}
                                    />
                                </label>
                            </div>
                        )}
                        {showResetButton && (
                            <div style={{ marginLeft: "20px" }}>
                                <ActionButton
                                    label="Reset Student"
                                    onClick={() => {
                                        resetAutoSuggest();
                                    }}
                                    tabIndex={0}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <OptionList
                filteredOptions={filteredOptions}
                formState={formState}
                handleChooseStudent={handleChooseStudent}
                resetAutoSuggest={resetAutoSuggest}
                showOptions={showOptions}
            />
        </div>
    );
};

AutoSuggest.propTypes = {
    formDispatch: PropTypes.func.isRequired,
    formState: PropTypes.oneOfType([PropTypes.object]).isRequired,
    handleChange: PropTypes.func.isRequired,
    id: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    selectedStudent: PropTypes.oneOfType([PropTypes.object]),
    setLoader: PropTypes.func.isRequired,
    setResultMessages: PropTypes.func.isRequired,
    setSelectedStudent: PropTypes.func.isRequired,
    setStudentSearchResults: PropTypes.func,
    studentSearchResults: PropTypes.oneOfType([PropTypes.object])
};

AutoSuggest.defaultProps = {
    id: "",
    label: "",
    selectedStudent: null,
    setStudentSearchResults: null,
    studentSearchResults: []
};

export default AutoSuggest;
